import React, { useCallback, useEffect, useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import {
  cancelSkillAgentSearch,
  clearSkillAgentSearchPhotos,
  deleteSkillAgentSearch,
  deleteSkillAgentSearchItems,
  fetchSkillAgentSearchItemBlobUrl,
  getSkillAgentSearchById,
  getSkillAgentSearchesMeta,
  listSkillAgentSearchItems,
  runSkillAgentSearch,
  updateSkillAgentSearch,
} from "../../api/Skills/skillAgentSearchesApi";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import { searchStatusLabel } from "./skillAgentTaskTree";
import SkillAgentBrandSourcesFields, {
  buildDefaultUrlSelection,
} from "./skillAgentBrandSourcesFields";

function SearchThumb({ item, onOpen, selected, onToggleSelect, selectable }) {
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        objectUrl = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.id]);

  return (
    <Box
      onClick={() => src && onOpen?.(item)}
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? "error.main" : "divider",
        bgcolor: "background.paper",
        cursor: src ? "zoom-in" : "default",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": src
          ? {
              borderColor: selected ? "error.main" : "primary.main",
              boxShadow: (t) =>
                `0 4px 14px ${alpha(t.palette.primary.main, 0.18)}`,
            }
          : undefined,
      }}
    >
      {selectable ? (
        <Checkbox
          size="small"
          checked={!!selected}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            onToggleSelect?.(item, e.target.checked);
          }}
          sx={{
            position: "absolute",
            top: 2,
            left: 2,
            zIndex: 2,
            p: 0.35,
            bgcolor: alpha("#fff", 0.85),
            borderRadius: 1,
            "&:hover": { bgcolor: "#fff" },
          }}
          inputProps={{ "aria-label": "Seleccionar foto" }}
        />
      ) : null}
      <Box
        sx={{
          height: 140,
          bgcolor: "action.hover",
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={item.Title || item.BrandName || ""}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            onError={() => setFailed(true)}
          />
        ) : failed ? (
          <Typography variant="caption" color="text.secondary">
            Sin preview
          </Typography>
        ) : (
          <CircularProgress size={18} />
        )}
      </Box>
      <Box sx={{ p: 1 }}>
        <Typography variant="caption" fontWeight={700} noWrap display="block">
          {item.Title || item.BrandName || "img"}
        </Typography>
      </Box>
    </Box>
  );
}

function SearchPhotoLightbox({
  items,
  index,
  onClose,
  onIndexChange,
  onDeleteCurrent,
  deleting,
}) {
  const item = index != null && items[index] ? items[index] : null;
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!item?.id) {
      setSrc("");
      return undefined;
    }
    let revoked = false;
    let objectUrl = "";
    setSrc("");
    setFailed(false);
    (async () => {
      try {
        objectUrl = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item?.id]);

  useEffect(() => {
    if (item == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") onIndexChange?.(Math.max(0, index - 1));
      if (e.key === "ArrowRight")
        onIndexChange?.(Math.min(items.length - 1, index + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, index, items.length, onClose, onIndexChange]);

  if (!item) return null;

  const driveHref = item.DriveFileUrl || null;
  const sourceHref = item.SourceUrl || null;
  const canPrev = index > 0;
  const canNext = index < items.length - 1;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "background.paper",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>
            {item.Title || item.BrandName || "Foto"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[item.BrandName, `${index + 1} / ${items.length}`]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Box>
        {sourceHref ? (
          <Button
            size="small"
            component={Link}
            href={sourceHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewOutlinedIcon />}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Origen
          </Button>
        ) : null}
        {driveHref ? (
          <Button
            size="small"
            component={Link}
            href={driveHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewOutlinedIcon />}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Drive
          </Button>
        ) : null}
        {onDeleteCurrent ? (
          <Button
            size="small"
            color="error"
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            onClick={() => onDeleteCurrent(item)}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Borrar
          </Button>
        ) : null}
        <IconButton onClick={onClose} size="small" aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          p: 0,
          position: "relative",
          bgcolor: alpha("#000", 0.92),
          minHeight: { xs: 320, md: 560 },
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={item.Title || item.BrandName || ""}
            sx={{
              maxWidth: "100%",
              maxHeight: { xs: "70vh", md: "78vh" },
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : failed ? (
          <Typography color="grey.400">No se pudo cargar la foto</Typography>
        ) : (
          <CircularProgress sx={{ color: "grey.200" }} />
        )}
        <IconButton
          disabled={!canPrev}
          onClick={() => onIndexChange?.(index - 1)}
          sx={{
            position: "absolute",
            left: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: alpha("#fff", 0.12),
            color: "#fff",
            "&:hover": { bgcolor: alpha("#fff", 0.22) },
            "&.Mui-disabled": { color: alpha("#fff", 0.25) },
          }}
          aria-label="Anterior"
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          disabled={!canNext}
          onClick={() => onIndexChange?.(index + 1)}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: alpha("#fff", 0.12),
            color: "#fff",
            "&:hover": { bgcolor: alpha("#fff", 0.22) },
            "&.Mui-disabled": { color: alpha("#fff", 0.25) },
          }}
          aria-label="Siguiente"
        >
          <ChevronRightIcon />
        </IconButton>
      </DialogContent>
    </Dialog>
  );
}

export default function SkillAgentSearchDetail({
  searchId,
  onChanged,
  onDeleted,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [search, setSearch] = useState(null);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [selectedDeleteOpen, setSelectedDeleteOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [catalogBrands, setCatalogBrands] = useState([]);
  const [editForm, setEditForm] = useState({
    Titulo: "",
    Tipologia: "",
    brandIds: [],
    urlSelection: {},
    customUrls: "",
    maxImagesPerBrand: "3",
    maxAgeDays: "60",
  });

  const images = useMemo(
    () => (Array.isArray(items) ? items.filter((i) => i.Kind === "image") : []),
    [items],
  );

  const selectedCount = selectedIds.size;

  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchId]);

  useEffect(() => {
    const valid = new Set(images.map((i) => Number(i.id)));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [images]);

  const toggleSelect = useCallback((item, on) => {
    const id = Number(item.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const selectAllPhotos = () => {
    setSelectedIds(new Set(images.map((i) => Number(i.id))));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const load = useCallback(async () => {
    if (!searchId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getSkillAgentSearchById(searchId);
      setSearch(data);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setRunning(data?.Status === "running");
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo cargar el search.");
    } finally {
      setLoading(false);
    }
  }, [searchId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getSkillAgentSearchesMeta()
      .then((m) => {
        const list = Array.isArray(m?.brands) ? m.brands : [];
        setCatalogBrands(
          [...list].sort((a, b) =>
            String(a.name || a.id).localeCompare(String(b.name || b.id), "es", {
              sensitivity: "base",
            }),
          ),
        );
      })
      .catch(() => setCatalogBrands([]));
  }, []);

  useEffect(() => {
    if (!searchId || !running) return undefined;
    const t = setInterval(async () => {
      try {
        const data = await listSkillAgentSearchItems(searchId);
        if (data?.status && data.status !== "running") {
          setRunning(false);
          await load();
          onChanged?.();
          if (data.status === "done") {
            setOkMsg(
              data.items?.length
                ? "Search listo (fotos en Drive)."
                : "Search terminado.",
            );
          } else if (data.status === "error") {
            setError("Falló el search.");
          }
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [searchId, running, load, onChanged]);

  const openEdit = () => {
    const c =
      search?.ConstraintsJson && typeof search.ConstraintsJson === "object"
        ? search.ConstraintsJson
        : {};
    const brands = Array.isArray(c.researchBrands) ? c.researchBrands : [];
    const brandIds = brands.length ? brands : catalogBrands.map((b) => b.id);
    const urls = Array.isArray(c.customUrls) ? c.customUrls.join("\n") : "";
    const urlSelection =
      c.brandUrlSelection && typeof c.brandUrlSelection === "object"
        ? c.brandUrlSelection
        : buildDefaultUrlSelection(catalogBrands, brandIds);
    setEditForm({
      Titulo: search?.Titulo || "",
      Tipologia: search?.Tipologia || c.tipología || "",
      brandIds,
      urlSelection,
      customUrls: urls,
      maxImagesPerBrand: String(c.maxImagesPerBrand || 3),
      maxAgeDays: String(c.maxAgeDays != null ? c.maxAgeDays : 60),
    });
    setEditOpen(true);
  };

  const handleSaveParams = async ({ andRun = false } = {}) => {
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      const customUrls = String(editForm.customUrls || "")
        .split(/[\n,]+/)
        .map((u) => u.trim())
        .filter(Boolean);
      const researchBrands = Array.isArray(editForm.brandIds)
        ? editForm.brandIds
        : [];
      if (!researchBrands.length && !customUrls.length) {
        setError("Marca al menos una marca o URL.");
        setBusy(false);
        return;
      }
      const prev =
        search?.ConstraintsJson && typeof search.ConstraintsJson === "object"
          ? search.ConstraintsJson
          : {};
      const updated = await updateSkillAgentSearch(searchId, {
        Titulo: editForm.Titulo.trim() || search.Titulo,
        Tipologia: editForm.Tipologia || null,
        ConstraintsJson: {
          ...prev,
          tipología: editForm.Tipologia || "",
          researchBrands,
          brandUrlSelection: editForm.urlSelection || {},
          customUrls,
          maxImagesPerBrand: Number(editForm.maxImagesPerBrand) || 3,
          maxAgeDays: Number(editForm.maxAgeDays) || 60,
          preferNewest: prev.preferNewest !== false,
          skipKnown: prev.skipKnown !== false,
        },
      });
      setSearch((p) => ({ ...p, ...updated }));
      setEditOpen(false);
      setOkMsg("Parámetros guardados.");
      onChanged?.();
      if (andRun) {
        await runSkillAgentSearch(searchId, {
          customerFolderName: search?.CustomerName || undefined,
        });
        setRunning(true);
        setSearch((p) => (p ? { ...p, Status: "running" } : p));
        setOkMsg("Relanzado con los nuevos parámetros…");
      }
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar.");
    } finally {
      setBusy(false);
    }
  };

  const handleRun = async () => {
    setError("");
    setOkMsg("");
    try {
      await runSkillAgentSearch(searchId, {
        customerFolderName: search?.CustomerName || undefined,
      });
      setRunning(true);
      setSearch((p) => (p ? { ...p, Status: "running" } : p));
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo lanzar.");
    }
  };

  const handleCancel = async () => {
    try {
      await cancelSkillAgentSearch(searchId);
      setRunning(false);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo cancelar.");
    }
  };

  const handleClearPhotos = async () => {
    setBusy(true);
    setError("");
    try {
      await clearSkillAgentSearchPhotos(searchId);
      setClearOpen(false);
      setSelectedIds(new Set());
      await load();
      onChanged?.();
      setOkMsg("Fotos borradas. Puedes cambiar parámetros y relanzar.");
    } catch (err) {
      setError(
        err?.response?.data?.error || "No se pudieron borrar las fotos.",
      );
    } finally {
      setBusy(false);
    }
  };

  const applyDeletedItems = (data, deletedIds) => {
    if (Array.isArray(data?.items)) {
      setItems(data.items);
    } else {
      const gone = new Set(deletedIds.map(Number));
      setItems((prev) => prev.filter((i) => !gone.has(Number(i.id))));
    }
    if (data?.search) setSearch(data.search);
    setSelectedIds((prev) => {
      const gone = new Set(deletedIds.map(Number));
      return new Set([...prev].filter((id) => !gone.has(id)));
    });
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBusy(true);
    setError("");
    try {
      const data = await deleteSkillAgentSearchItems(searchId, ids);
      setSelectedDeleteOpen(false);
      applyDeletedItems(data, ids);
      onChanged?.();
      setOkMsg(
        `${data?.deleted ?? ids.length} foto${ids.length === 1 ? "" : "s"} borrada${ids.length === 1 ? "" : "s"} (CRM + Drive).`,
      );
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "No se pudieron borrar las fotos seleccionadas.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteLightboxItem = async (item) => {
    if (!item?.id) return;
    setBusy(true);
    setError("");
    try {
      const id = Number(item.id);
      const data = await deleteSkillAgentSearchItems(searchId, [id]);
      const remaining = Array.isArray(data?.items)
        ? data.items.filter((i) => i.Kind === "image")
        : images.filter((i) => Number(i.id) !== id);
      applyDeletedItems(data, [id]);
      onChanged?.();
      if (!remaining.length) {
        setLightboxIndex(null);
      } else {
        const nextIdx = Math.min(lightboxIndex ?? 0, remaining.length - 1);
        setLightboxIndex(nextIdx);
      }
      setOkMsg("Foto borrada (CRM + Drive).");
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo borrar la foto.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    setError("");
    try {
      await deleteSkillAgentSearch(searchId);
      setDeleteOpen(false);
      onDeleted?.(searchId);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo eliminar.");
      setBusy(false);
    }
  };

  if (loading && !search) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!search) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Search no encontrado.
      </Alert>
    );
  }

  const locked = running || busy;

  return (
    <Stack spacing={2.25} sx={{ p: { xs: 2, md: 2.5 } }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 8,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          py: 1.25,
          px: { xs: 0.5, md: 0 },
          mb: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.92),
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.35 }}
          >
            <Typography
              variant="h6"
              fontWeight={800}
              noWrap
              sx={{ lineHeight: 1.25 }}
            >
              {search.Titulo}
            </Typography>
            <Chip
              size="small"
              label={searchStatusLabel(search.Status)}
              color={
                search.Status === "done"
                  ? "success"
                  : search.Status === "error"
                    ? "error"
                    : search.Status === "running"
                      ? "warning"
                      : "default"
              }
              sx={{
                height: 24,
                borderRadius: 1.25,
                fontWeight: 800,
                flexShrink: 0,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" noWrap>
            {[search.CustomerName, search.Temporada, search.Tipologia]
              .filter(Boolean)
              .join(" · ") || "SEARCH library"}
          </Typography>
          {search.ResultSummary && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.35 }}
            >
              {search.ResultSummary}
            </Typography>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          sx={{
            flexShrink: 0,
            "& .MuiButton-root": {
              height: 34,
              minHeight: 34,
              py: 0,
              px: 1.35,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
            },
          }}
        >
          {search.DriveFolderUrl && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewOutlinedIcon />}
              href={search.DriveFolderUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Drive
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            disabled={locked}
            onClick={openEdit}
          >
            Parámetros
          </Button>
          {running ? (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              startIcon={<StopOutlinedIcon />}
              onClick={handleCancel}
            >
              Cancelar
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowOutlinedIcon />}
              onClick={handleRun}
              disabled={locked}
              sx={{
                px: 1.75,
                boxShadow: (t) =>
                  `0 2px 10px ${alpha(t.palette.primary.main, 0.28)}`,
              }}
            >
              {images.length ? "Relanzar" : "Lanzar"}
            </Button>
          )}
          <Button
            size="small"
            color="error"
            variant="text"
            startIcon={<DeleteOutlineIcon />}
            disabled={locked}
            onClick={() => setDeleteOpen(true)}
          >
            Eliminar
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" onClose={() => setOkMsg("")}>
          {okMsg}
        </Alert>
      )}

      {running && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">
            Rastreando webs y subiendo fotos a Drive…
          </Typography>
        </Box>
      )}

      {(() => {
        const c =
          search.ConstraintsJson && typeof search.ConstraintsJson === "object"
            ? search.ConstraintsJson
            : {};
        const brands = (Array.isArray(c.researchBrands) ? c.researchBrands : [])
          .slice()
          .sort((a, b) => {
            const nameA = catalogBrands.find((x) => x.id === a)?.name || a;
            const nameB = catalogBrands.find((x) => x.id === b)?.name || b;
            return String(nameA).localeCompare(String(nameB), "es", {
              sensitivity: "base",
            });
          });
        const extras = Array.isArray(c.customUrls) ? c.customUrls : [];
        const perBrand = c.maxImagesPerBrand || 3;
        const ageDays = c.maxAgeDays != null ? c.maxAgeDays : 60;
        if (!brands.length && !extras.length) return null;
        return (
          <Box>
            <Typography
              variant="caption"
              fontWeight={800}
              color="text.secondary"
              sx={{
                display: "block",
                mb: 0.75,
                letterSpacing: 0.04,
                textTransform: "uppercase",
              }}
            >
              Fuentes · {perBrand} imgs/marca · novedades ≤{ageDays}d · sin
              duplicados · {brands.length + extras.length}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {brands.map((id) => (
                <Chip
                  key={id}
                  size="small"
                  label={id}
                  variant="outlined"
                  sx={{
                    height: 22,
                    borderRadius: 1.25,
                    fontSize: "0.68rem",
                    "& .MuiChip-label": { px: 0.85 },
                  }}
                />
              ))}
              {extras.map((u) => (
                <Chip
                  key={u}
                  size="small"
                  color="info"
                  variant="outlined"
                  label={String(u)
                    .replace(/^https?:\/\//, "")
                    .slice(0, 36)}
                  sx={{
                    height: 22,
                    borderRadius: 1.25,
                    fontSize: "0.68rem",
                    "& .MuiChip-label": { px: 0.85 },
                  }}
                />
              ))}
            </Stack>
          </Box>
        );
      })()}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
      >
        <Typography variant="subtitle2" fontWeight={800}>
          Fotos ({images.length})
          {selectedCount > 0 ? ` · ${selectedCount} sel.` : ""}
        </Typography>
        {images.length > 0 && !running && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {selectedCount > 0 ? (
              <>
                <Button
                  size="small"
                  onClick={clearSelection}
                  disabled={locked}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Quitar selección
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  disabled={locked}
                  onClick={() => setSelectedDeleteOpen(true)}
                  startIcon={<DeleteOutlineIcon />}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Borrar {selectedCount}
                </Button>
              </>
            ) : (
              <Button
                size="small"
                disabled={locked}
                onClick={selectAllPhotos}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Seleccionar todas
              </Button>
            )}
            <Button
              size="small"
              color="warning"
              onClick={() => setClearOpen(true)}
              disabled={locked}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Borrar todas
            </Button>
          </Stack>
        )}
      </Stack>
      {images.length > 0 && !running ? (
        <Typography variant="caption" color="text.secondary">
          Marca el check de cada foto (o en el lightbox → Borrar). Se elimina
          del CRM, caché local y papelera de Drive.
        </Typography>
      ) : null}
      {images.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Sin fotos. Lanza para bajar solo novedades (~60 días). Relanzar suma
          solo lo nuevo; no vuelve a bajar URLs ya guardadas de este
          cliente/temporada.
        </Typography>
      ) : (
        (() => {
          const byBrand = new Map();
          for (const img of images) {
            const key = img.BrandName || img.BrandId || "Sin marca";
            if (!byBrand.has(key)) byBrand.set(key, []);
            byBrand.get(key).push(img);
          }
          const brandEntries = [...byBrand.entries()].sort((a, b) =>
            String(a[0]).localeCompare(String(b[0]), "es", {
              sensitivity: "base",
            }),
          );
          return (
            <Stack spacing={2}>
              {brandEntries.map(([brand, imgs]) => (
                <Box key={brand}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                    color="text.secondary"
                  >
                    {brand} · {imgs.length}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(140px, 1fr))",
                      gap: 1.25,
                    }}
                  >
                    {imgs.map((img) => (
                      <SearchThumb
                        key={img.id}
                        item={img}
                        selectable={!running}
                        selected={selectedIds.has(Number(img.id))}
                        onToggleSelect={toggleSelect}
                        onOpen={(it) => {
                          const idx = images.findIndex(
                            (x) => Number(x.id) === Number(it.id),
                          );
                          if (idx >= 0) setLightboxIndex(idx);
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
          );
        })()
      )}

      {/* Edit params */}
      <Dialog
        open={editOpen}
        onClose={() => !busy && setEditOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Parámetros del SEARCH</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {[search.CustomerName, search.Temporada]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
            <TextField
              size="small"
              label="Título"
              value={editForm.Titulo}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, Titulo: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label="Tipología / keywords"
              value={editForm.Tipologia}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, Tipologia: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
              helperText="Separa por comas: Jacket, bomber, fleece, overshirt, jkt…"
              sx={pageFieldSx}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                label="Imgs/marca"
                type="number"
                inputProps={{ min: 1, max: 12 }}
                value={editForm.maxImagesPerBrand}
                onChange={(e) =>
                  setEditForm((p) => ({
                    ...p,
                    maxImagesPerBrand: e.target.value,
                  }))
                }
                sx={{ width: 120, ...pageFieldSx }}
              />
              <TextField
                size="small"
                label="Novedades (días)"
                type="number"
                inputProps={{ min: 7, max: 365 }}
                value={editForm.maxAgeDays}
                onChange={(e) =>
                  setEditForm((p) => ({
                    ...p,
                    maxAgeDays: e.target.value,
                  }))
                }
                helperText="Solo productos recientes"
                sx={{ width: 150, ...pageFieldSx }}
              />
            </Stack>
            <SkillAgentBrandSourcesFields
              brands={catalogBrands}
              brandIds={editForm.brandIds}
              urlSelection={editForm.urlSelection}
              onBrandIdsChange={(brandIds) =>
                setEditForm((p) => ({ ...p, brandIds }))
              }
              onUrlSelectionChange={(urlSelection) =>
                setEditForm((p) => ({ ...p, urlSelection }))
              }
            />
            <TextField
              size="small"
              label="Webs extra"
              multiline
              minRows={2}
              value={editForm.customUrls}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, customUrls: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => setEditOpen(false)} disabled={busy}>
            Cerrar
          </Button>
          <Button
            disabled={busy}
            onClick={() => handleSaveParams({ andRun: false })}
          >
            Guardar
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={busy}
            onClick={() => handleSaveParams({ andRun: true })}
          >
            Guardar y relanzar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear all photos confirm */}
      <Dialog
        open={clearOpen}
        onClose={() => !busy && setClearOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Borrar todas las fotos</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Se quitarán las fotos de este SEARCH en el CRM y pasarán a la{" "}
            <b>papelera de Drive</b>. El SEARCH se queda para que puedas
            relanzar.
          </Typography>
          {images.length > 0 ? (
            <Typography variant="body2" sx={{ mt: 1.25 }} fontWeight={700}>
              {images.length} foto{images.length === 1 ? "" : "s"} a borrar
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClearOpen(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={busy}
            onClick={handleClearPhotos}
            startIcon={
              busy ? <CircularProgress size={14} color="inherit" /> : null
            }
          >
            Borrar todas
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete selected photos */}
      <Dialog
        open={selectedDeleteOpen}
        onClose={() => !busy && setSelectedDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Borrar seleccionadas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Se eliminan del CRM, de la caché local y van a la{" "}
            <b>papelera de Drive</b>.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.25 }} fontWeight={700}>
            {selectedCount} foto{selectedCount === 1 ? "" : "s"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelectedDeleteOpen(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy || selectedCount === 0}
            onClick={handleDeleteSelected}
            startIcon={
              busy ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
          >
            Borrar {selectedCount}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={deleteOpen}
        onClose={() => !busy && setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Eliminar SEARCH</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se borrará del CRM y la carpeta/fotos de Drive irán a la{" "}
            <b>papelera</b>. ¿Seguro?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={handleDelete}
          >
            Eliminar todo
          </Button>
        </DialogActions>
      </Dialog>

      <SearchPhotoLightbox
        items={images}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
        onDeleteCurrent={running ? undefined : handleDeleteLightboxItem}
        deleting={busy}
      />
    </Stack>
  );
}
